import * as XLSX from "xlsx";

const SHA256_HEX = async (arrayBuffer) => {
  try {
    const buf = await crypto.subtle.digest("SHA-256", arrayBuffer);
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch (_) {
    return "";
  }
};

const VARIETY_LABEL = (s) => s.grass_variety;

const REASON_LABELS = {
  incorrect_entry: "Incorrect entry",
  customer_cancelled: "Customer cancelled",
  other: "Other",
};

const formatStatus = (row) => {
  if (!row?.voided) return "Completed";
  const label = REASON_LABELS[row.void_reason] || row.void_reason || "Cancelled";
  if (row.void_reason === "other" && row.void_reason_text) {
    return `Cancelled · Other: ${row.void_reason_text}`;
  }
  if (row.credit_note_number) return `Cancelled · ${label} · ${row.credit_note_number}`;
  return `Cancelled · ${label}`;
};

function buildFinancialsSheet(sales, expenses) {
  const rows = [];
  rows.push({ Section: "── SALES ──" });
  sales.forEach((s) => {
    const rate = s.customer_type === "Human Food" ? 0.06 : 0.25;
    const voided = !!s.voided;
    const origNet = +s.net_price_sek;
    const effNet = voided ? 0 : origNet;
    const moms = +(effNet * rate).toFixed(2);
    rows.push({
      Section: "Sale",
      Status: formatStatus(s),
      Date: s.date,
      ID: s.id,
      Description: `${s.customer_name} · ${VARIETY_LABEL(s)}`,
      "Customer Type": s.customer_type,
      "Quantity (Trays)": s.quantity_trays,
      "Weight (lbs)": s.total_weight_lbs,
      "Original Net (SEK)": +origNet.toFixed(2),
      "Net (SEK)": +effNet.toFixed(2),
      "Moms Rate": `${rate * 100}%`,
      "Moms (SEK)": moms,
      "Gross (SEK)": +(effNet + moms).toFixed(2),
    });
  });
  rows.push({});
  rows.push({ Section: "── EXPENSES ──" });
  expenses.forEach((e) => {
    rows.push({
      Section: "Expense",
      Status: "Completed",
      Date: e.date,
      ID: e.id,
      Description: e.notes || e.category,
      Category: e.category,
      "Amount (SEK)": e.amount_sek,
    });
  });
  // Totals — ONLY active (non-voided) sales contribute. Voided sales are recorded
  // as 0,00 SEK in the rows above so the user can see them on the sheet but Skatteverket
  // doesn't end up taxing reversed transactions.
  rows.push({});
  const activeSales = sales.filter((s) => !s.voided);
  const cancelledSales = sales.filter((s) => s.voided);
  const netRev = activeSales.reduce((a, s) => a + s.net_price_sek, 0);
  const moms6 = activeSales.filter((s) => s.customer_type === "Human Food").reduce((a, s) => a + s.net_price_sek * 0.06, 0);
  const moms25 = activeSales.filter((s) => s.customer_type !== "Human Food").reduce((a, s) => a + s.net_price_sek * 0.25, 0);
  const totalExp = expenses.reduce((a, e) => a + e.amount_sek, 0);
  const cancelledOriginal = cancelledSales.reduce((a, s) => a + (s.net_price_sek || 0), 0);
  rows.push({ Section: "── TOTALS (active only) ──" });
  rows.push({ Section: "Net Revenue (excl. Moms)", "Net (SEK)": +netRev.toFixed(2) });
  rows.push({ Section: "6% Output Moms", "Moms (SEK)": +moms6.toFixed(2) });
  rows.push({ Section: "25% Output Moms", "Moms (SEK)": +moms25.toFixed(2) });
  rows.push({ Section: "Total Moms Owed", "Moms (SEK)": +(moms6 + moms25).toFixed(2) });
  rows.push({ Section: "Gross Revenue (incl. Moms)", "Gross (SEK)": +(netRev + moms6 + moms25).toFixed(2) });
  rows.push({ Section: "Total Expenditure", "Amount (SEK)": +totalExp.toFixed(2) });
  rows.push({ Section: "Net Profit Before Tax", "Net (SEK)": +(netRev - totalExp).toFixed(2) });
  rows.push({});
  rows.push({ Section: "── CANCELLED (excluded from totals) ──" });
  rows.push({ Section: "Cancelled transactions", "Quantity (Trays)": cancelledSales.length, "Original Net (SEK)": +cancelledOriginal.toFixed(2), "Net (SEK)": 0 });
  return XLSX.utils.json_to_sheet(rows);
}

function buildInventorySheet(sales, expenses, settings) {
  const ratio = settings?.seed_per_tray_ratio ?? 0.25;
  const events = [];
  expenses.filter((e) => e.category === "Grains/Seeds").forEach((e) => {
    events.push({
      Date: e.date,
      Variety: e.grain_variety || "—",
      Type: "GRAIN IN",
      Source: e.supplier_name || "—",
      "Batch #": e.batch_number || "—",
      "Δ Stock (kg)": +(e.quantity_kg || 0).toFixed(2),
      "Price/kg (SEK)": e.price_per_kg || "",
      "Amount (SEK)": e.amount_sek,
      Note: e.notes || "",
    });
  });
  // Inventory: voided sales had their grain restored at void time, so they
  // contribute 0 to seed-out. We still show them as a 0 event for audit clarity.
  sales.forEach((s) => {
    const voided = !!s.voided;
    const used = voided ? 0 : +(s.quantity_trays * ratio).toFixed(2);
    events.push({
      Date: s.date,
      Variety: s.grass_variety,
      Type: voided ? "SEED OUT (VOID)" : "SEED OUT",
      Source: s.customer_name,
      "Batch #": "—",
      "Δ Stock (kg)": -used,
      "Trays": voided ? 0 : s.quantity_trays,
      Note: voided ? `Cancelled — grain restored` : `${s.quantity_trays} trays × ${ratio} kg/tray`,
    });
  });
  events.sort((a, b) => (a.Date > b.Date ? 1 : a.Date < b.Date ? -1 : 0));
  // Running balance per-variety
  const running = { Wheat: 0, Barley: 0, Corn: 0 };
  events.forEach((ev) => {
    const v = ev.Variety;
    if (v in running) {
      running[v] += ev["Δ Stock (kg)"];
      ev[`Running ${v} (kg)`] = +running[v].toFixed(2);
    }
  });
  events.push({});
  events.push({ Date: "── CURRENT STOCK ──" });
  const stocks = settings?.current_grain_stock || {};
  Object.keys(stocks).forEach((v) => {
    events.push({ Date: v, "Δ Stock (kg)": +(stocks[v] || 0).toFixed(2) });
  });
  events.push({ Date: "Seed-per-Tray Ratio", "Δ Stock (kg)": ratio });
  return XLSX.utils.json_to_sheet(events);
}

export function buildLedgerWorkbook(sales, expenses, settings) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildFinancialsSheet(sales, expenses), "Financials & Taxes");
  XLSX.utils.book_append_sheet(wb, buildInventorySheet(sales, expenses, settings), "Inventory & Supply Chain");
  // Append a tamper-evident provenance sheet (read-only by convention).
  const activeSalesCount = sales.filter((s) => !s.voided).length;
  const voidedSalesCount = sales.filter((s) => s.voided).length;
  const provRows = [
    { Field: "App", Value: "The NordHim Ledger (TNL)" },
    { Field: "Generated at (UTC)", Value: new Date().toISOString() },
    { Field: "Business", Value: settings?.business_name || "" },
    { Field: "Org. Nr", Value: settings?.org_number || "" },
    { Field: "Moms ID", Value: settings?.moms_id || "" },
    { Field: "Production started", Value: settings?.production_started_at || "Not yet started" },
    { Field: "Retention required until", Value: settings?.retention_required_until || "—" },
    { Field: "Sales rows (active)", Value: activeSalesCount },
    { Field: "Sales rows (cancelled / kreditfaktura)", Value: voidedSalesCount },
    { Field: "Expenses rows", Value: expenses.length },
    { Field: "", Value: "" },
    { Field: "Tamper-evident SHA-256", Value: "(computed on file below)" },
    { Field: "Verify", Value: "If this hash differs from the .sha256 sidecar, the file has been modified." },
  ];
  const provWs = XLSX.utils.json_to_sheet(provRows, { header: ["Field", "Value"] });
  provWs["!cols"] = [{ wch: 32 }, { wch: 60 }];
  XLSX.utils.book_append_sheet(wb, provWs, "Provenance");
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const filename = `mgl_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
  return { blob, filename, _bytes: wbout };
}

export async function fingerprintBlob(arrayBuffer) {
  return await SHA256_HEX(arrayBuffer);
}

export async function exportToExcel(sales, expenses, settings) {
  const { blob, filename } = buildLedgerWorkbook(sales, expenses, settings);
  return shareOrDownload(blob, filename, "The NordHim Ledger Export", "Financials & Inventory");
}

export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      // result is "data:<mime>;base64,XXXX"
      const idx = String(result).indexOf(",");
      resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export async function exportReceiptsToExcel(receipts, label) {
  const rows = receipts.map((r) => {
    const voided = !!r.voided;
    const subtotal = voided ? 0 : +r.subtotal;
    const vat = voided ? 0 : +r.vat_amount;
    const total = voided ? 0 : +r.total;
    return {
      Status: formatStatus(r),
      "Receipt #": r.number,
      "Credit Note #": r.credit_note_number || "",
      Date: r.date,
      "Payment Method": r.payment_method,
      Customer: r.customer_name || "",
      "Customer Email": r.customer_email || "",
      Item: r.item_name,
      "Weight (kg)": voided ? 0 : r.weight_kg,
      "Price/kg (SEK)": r.price_per_kg,
      "Original Subtotal (SEK)": +(r.subtotal || 0).toFixed(2),
      "Subtotal (SEK)": +subtotal.toFixed(2),
      [`VAT (${(r.vat_rate * 100).toFixed(0)}%) (SEK)`]: +vat.toFixed(2),
      "Original Total (SEK)": +(r.total || 0).toFixed(2),
      "Total (SEK)": +total.toFixed(2),
    };
  });
  // Totals — exclude voided
  const active = receipts.filter((r) => !r.voided);
  const voided = receipts.filter((r) => r.voided);
  const totalSubtotal = active.reduce((a, r) => a + r.subtotal, 0);
  const totalVat = active.reduce((a, r) => a + r.vat_amount, 0);
  const totalGross = active.reduce((a, r) => a + r.total, 0);
  const cancelledOriginalTotal = voided.reduce((a, r) => a + (r.total || 0), 0);
  rows.push({});
  rows.push({ Status: "── TOTAL (active only) ──", "Subtotal (SEK)": +totalSubtotal.toFixed(2), "Total (SEK)": +totalGross.toFixed(2) });
  rows.push({ Status: "VAT collected (active)", [`VAT (12%) (SEK)`]: +totalVat.toFixed(2) });
  rows.push({});
  rows.push({ Status: "── CANCELLED (excluded) ──", "Receipt #": `${voided.length} receipts`, "Original Total (SEK)": +cancelledOriginalTotal.toFixed(2), "Total (SEK)": 0 });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Receipts");
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const filename = `receipts_${label || "all"}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  return shareOrDownload(blob, filename, "Receipts Export", label || "Receipts bulk export");
}

export async function shareOrDownload(blob, filename, title, text) {
  const file = new File([blob], filename, { type: blob.type });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title, text });
      return { shared: true };
    } catch (e) {
      if (e?.name !== "AbortError") {
        triggerDownload(blob, filename);
        return { downloaded: true };
      }
      return { aborted: true };
    }
  }
  triggerDownload(blob, filename);
  return { downloaded: true };
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------------------
//  Quarterly Moms Filing pack — Skatteverket-ready zip bundle
// ---------------------------------------------------------------------------
// Returns the [startISO, endISO] date strings for a Swedish calendar quarter.
export function quarterRange(year, quarter) {
  const q = Math.max(1, Math.min(4, +quarter));
  const startMonth = (q - 1) * 3; // 0,3,6,9
  const start = new Date(Date.UTC(year, startMonth, 1));
  const end = new Date(Date.UTC(year, startMonth + 3, 0)); // last day of last month
  const iso = (d) => d.toISOString().slice(0, 10);
  return [iso(start), iso(end)];
}

const inRange = (dateStr, startISO, endISO) =>
  typeof dateStr === "string" && dateStr >= startISO && dateStr <= endISO;

/**
 * Build a Quarterly Moms Filing pack for the given year + quarter.
 *
 *   filing_pack_YYYY_QN/
 *     1_Ledger_YYYY_QN.xlsx                     — full Financials & Inventory workbook (filtered)
 *     2_Receipts/                               — every receipt PDF for the quarter (active + voided)
 *       Receipt_<number>.pdf
 *     3_Credit_Notes/                           — Kreditfaktura PDF for every voided receipt
 *       CreditNote_<number>.pdf
 *     README.txt                                — checklist + SHA-256 manifest
 *
 * @param {object} args
 * @param {number} args.year                     — e.g. 2026
 * @param {number} args.quarter                  — 1..4
 * @param {Array} args.sales                     — full sales list
 * @param {Array} args.expenses                  — full expenses list
 * @param {Array} args.receipts                  — full receipts list
 * @param {object} args.settings                 — business settings
 * @param {(id:string)=>Promise<Blob>} args.fetchReceiptPdf
 * @param {(id:string)=>Promise<Blob>} args.fetchCreditNotePdf
 * @param {(label:string,pct:number)=>void} [args.onProgress]
 */
export async function buildQuarterlyFilingPack({
  year, quarter, sales, expenses, receipts, settings,
  fetchReceiptPdf, fetchCreditNotePdf, onProgress,
}) {
  const JSZipMod = (await import("jszip")).default;
  const [startISO, endISO] = quarterRange(year, quarter);
  const label = `${year}_Q${quarter}`;

  const filteredSales = sales.filter((s) => inRange(s.date, startISO, endISO));
  const filteredExpenses = expenses.filter((e) => inRange(e.date, startISO, endISO));
  const filteredReceipts = receipts.filter((r) => inRange(r.date, startISO, endISO));
  const voidedReceipts = filteredReceipts.filter((r) => r.voided);

  onProgress?.("Building ledger workbook", 5);
  const { _bytes: xlsxBytes } = buildLedgerWorkbook(filteredSales, filteredExpenses, settings);

  const zip = new JSZipMod();
  const root = zip.folder(`filing_pack_${label}`);
  const xlsxName = `1_Ledger_${label}.xlsx`;
  root.file(xlsxName, xlsxBytes);

  const receiptsFolder = root.folder("2_Receipts");
  const creditFolder = root.folder("3_Credit_Notes");

  // Receipt PDFs — fetch one by one to keep memory low and surface progress.
  const totalSteps = filteredReceipts.length + voidedReceipts.length;
  let done = 0;
  for (const r of filteredReceipts) {
    try {
      const blob = await fetchReceiptPdf(r.id);
      const safe = (r.number || r.id).replace(/[^A-Za-z0-9_-]/g, "_");
      const buf = await blob.arrayBuffer();
      receiptsFolder.file(`Receipt_${safe}.pdf`, buf);
    } catch (_) {
      // Skip individual failures so a single bad PDF doesn't break the whole pack.
    }
    done += 1;
    onProgress?.(`Receipt PDFs (${done}/${totalSteps})`, 10 + (done / Math.max(1, totalSteps)) * 70);
  }
  for (const r of voidedReceipts) {
    try {
      const blob = await fetchCreditNotePdf(r.id);
      const idTag = (r.credit_note_number || r.number || r.id).replace(/[^A-Za-z0-9_-]/g, "_");
      const buf = await blob.arrayBuffer();
      creditFolder.file(`CreditNote_${idTag}.pdf`, buf);
    } catch (_) {
      // Skip
    }
    done += 1;
    onProgress?.(`Credit notes (${done}/${totalSteps})`, 10 + (done / Math.max(1, totalSteps)) * 70);
  }

  // Pre-zip SHA-256 manifest (audit hash of every file before bundling)
  const manifestLines = [];

  // SIE-4 — add the standard Swedish accounting interchange file for the year
  if (args.fetchSie4) {
    onProgress?.("Building SIE-4", 82);
    try {
      const sie4Blob = await args.fetchSie4(year);
      const sie4Text = await sie4Blob.text();
      root.file(`4_SIE4/sie4_${year}.se`, sie4Text);
    } catch (_) { /* skip on failure */ }
  }

  const files = Object.entries(zip.files).filter(([, f]) => !f.dir);
  for (const [name, f] of files) {
    const buf = await f.async("arraybuffer");
    const hash = await SHA256_HEX(buf);
    manifestLines.push(`${hash}  ${name}`);
  }

  // Totals summary mirrors the workbook (active-only)
  const active = filteredSales.filter((s) => !s.voided);
  const netRev = active.reduce((a, s) => a + s.net_price_sek, 0);
  const moms6 = active.filter((s) => s.customer_type === "Human Food").reduce((a, s) => a + s.net_price_sek * 0.06, 0);
  const moms25 = active.filter((s) => s.customer_type !== "Human Food").reduce((a, s) => a + s.net_price_sek * 0.25, 0);
  const totalExp = filteredExpenses.reduce((a, e) => a + e.amount_sek, 0);
  const fmt = (n) => `${(+n).toFixed(2)} SEK`;

  const readme = [
    `The NordHim Ledger — Quarterly Moms Filing Pack`,
    `============================================`,
    ``,
    `Business:            ${settings?.business_name || "—"}`,
    `Org. Nr:             ${settings?.org_number || "—"}`,
    `Moms-reg. Nr:        ${settings?.moms_id || "—"}`,
    `Period:              ${startISO}  →  ${endISO}   (${label})`,
    `Generated (UTC):     ${new Date().toISOString()}`,
    ``,
    `------------------------------------------------------------------`,
    `SUMMARY (active transactions only — voided rows excluded)`,
    `------------------------------------------------------------------`,
    `Sales (active):       ${active.length}`,
    `Sales (voided/cn):    ${filteredSales.length - active.length}`,
    `Receipts (issued):    ${filteredReceipts.length}`,
    `Receipts (voided):    ${voidedReceipts.length}`,
    `Expenses:             ${filteredExpenses.length}`,
    ``,
    `Net Revenue (excl. Moms):    ${fmt(netRev)}`,
    `6% Output Moms:              ${fmt(moms6)}`,
    `25% Output Moms:             ${fmt(moms25)}`,
    `Total Moms Owed:             ${fmt(moms6 + moms25)}`,
    `Gross Revenue (incl. Moms):  ${fmt(netRev + moms6 + moms25)}`,
    `Total Expenditure:           ${fmt(totalExp)}`,
    `Net Profit Before Tax:       ${fmt(netRev - totalExp)}`,
    ``,
    `------------------------------------------------------------------`,
    `FILING CHECKLIST — Skatteverket Moms e-tjänst`,
    `------------------------------------------------------------------`,
    `[ ] 1.  Open Skatteverket e-tjänst → "Mervärdesskatt".`,
    `[ ] 2.  Pick reporting period: ${startISO} – ${endISO}.`,
    `[ ] 3.  Field 05  (Försäljning belagd med moms): ${fmt(netRev)}`,
    `[ ] 4.  Field 10  (Utgående moms 25%):           ${fmt(moms25)}`,
    `[ ] 5.  Field 12  (Utgående moms 6%):            ${fmt(moms6)}`,
    `[ ] 6.  Attach the Excel workbook on demand if audited.`,
    `[ ] 7.  Keep this pack for 7 years (Bokföringslagen 5 kap.).`,
    ``,
    `------------------------------------------------------------------`,
    `SHA-256 MANIFEST  (verify file integrity)`,
    `------------------------------------------------------------------`,
    ...manifestLines,
    ``,
    `If any hash above changes after extraction, the file was modified.`,
  ].join("\n");
  root.file("README.txt", readme);

  onProgress?.("Compressing…", 90);
  const zipBlob = await zip.generateAsync({ type: "blob" });
  const filename = `TNL_FilingPack_${label}.zip`;
  onProgress?.("Done", 100);
  return { blob: zipBlob, filename, summary: { netRev, moms6, moms25, totalExp, active, voidedReceipts } };
}

export async function exportQuarterlyFilingPack(args) {
  const { blob, filename } = await buildQuarterlyFilingPack(args);
  return shareOrDownload(blob, filename, "Quarterly Moms Filing Pack", filename);
}
